import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiMapPin, FiTrendingUp, FiCheckCircle, FiAlertCircle, 
  FiArrowRight, FiArrowLeft, FiDollarSign, FiAward, 
  FiShield, FiTarget, FiBriefcase, FiRefreshCw, FiInfo, FiExternalLink
} = FiIcons;

// --- CONFIG & DATA ---

const SERVICES = [
  { id: 'dog-walking', label: 'Dog Walking', icon: '🐕' },
  { id: 'pet-sitting', label: 'Pet Sitting', icon: '🏠' },
  { id: 'boarding', label: 'Boarding', icon: '🌙' },
  { id: 'grooming', label: 'Grooming', icon: '✂️' },
  { id: 'training', label: 'Training', icon: '🎓' },
  { id: 'daycare', label: 'Daycare', icon: '🐾' }
];

const INCOME_GOALS = [
  { id: 'side', label: '$2,000/mo', value: 2000, sub: 'Side income' },
  { id: 'part', label: '$3,500/mo', value: 3500, sub: 'Part-time income' },
  { id: 'full', label: '$5,000/mo', value: 5000, sub: 'Full-time income' },
  { id: 'growth', label: '$7,500+/mo', value: 7500, sub: 'Growing business' }
];

const PRICING_DATA = {
  'dog-walking': {
    unit: 'walk',
    sessionsPerClient: 8,
    national: { '15': 20.20, '30': 29.57, '60': 46.39 },
    high: { '15': 22.30, '30': 33.08, '60': 47.68 },
    mid: { '15': 20.55, '30': 29.68, '60': 49.72 },
    low: { '15': 19.68, '30': 27.46, '60': 46.17 },
    addons: { '2-dogs': 6, '3-plus-dogs': 11 }
  },
  'pet-sitting': {
    unit: 'visit',
    sessionsPerClient: 8,
    national: { '15': 20.20, '30': 37.86, '60': 52.00 },
    high: { '15': 22.00, '30': 43.07, '60': 58.00 },
    mid: { '15': 20.00, '30': 38.73, '60': 54.00 },
    low: { '15': 18.00, '30': 34.31, '60': 48.00 },
    modifiers: { 'cat': 0.88 }
  },
  'boarding': {
    unit: 'night',
    sessionsPerClient: 3,
    national: { 'kennel': 40, 'sitter': 35, 'in-home': 60, 'overnight': 67 },
    high: { 'kennel': 65, 'sitter': 55, 'in-home': 85, 'overnight': 80 },
    mid: { 'kennel': 43, 'sitter': 37, 'in-home': 55, 'overnight': 65 },
    low: { 'kennel': 37, 'sitter': 32, 'in-home': 47, 'overnight': 55 }
  },
  'grooming': {
    unit: 'session',
    sessionsPerClient: 1.5,
    national: { 'small': 65, 'medium': 95, 'large': 130, 'xlarge': 165 },
    high: { 'small': 80, 'medium': 120, 'large': 160, 'xlarge': 200 },
    mid: { 'small': 60, 'medium': 90, 'large': 125, 'xlarge': 160 },
    low: { 'small': 55, 'medium': 80, 'large': 110, 'xlarge': 145 },
    addons: { 'mobile': 20 },
    modifiers: { 'bath-only': -25 }
  },
  'training': {
    unit: 'session',
    sessionsPerClient: 4,
    national: { 'group': 55, 'private': 100, 'board': 800 },
    high: { 'group': 70, 'private': 140, 'board': 1100 },
    mid: { 'group': 55, 'private': 100, 'board': 800 },
    low: { 'group': 45, 'private': 80, 'board': 650 }
  },
  'daycare': {
    unit: 'day',
    sessionsPerClient: 12,
    national: { 'full': 42, 'half': 26 },
    high: { 'full': 58, 'half': 36 },
    mid: { 'full': 42, 'half': 26 },
    low: { 'full': 34, 'half': 21 }
  }
};

const ZIP_RANGES = {
  high: ['900', '901', '902', '903', '904', '905', '906', '907', '908', '909', '910', '911', '912', '913', '914', '915', '916', '917', '918', '919', '920', '921', '922', '923', '924', '925', '926', '927', '928', '929', '930', '931', '932', '933', '934', '935', '936', '937', '938', '939', '940', '941', '942', '943', '944', '945', '946', '947', '948', '949', '950', '951', '952', '953', '954', '955', '956', '957', '958', '959', '960', '961', '100', '101', '102', '103', '104', '980', '981', '982', '983', '984', '985', '986', '800', '801', '802', '803', '804', '020', '021', '022', '023', '024', '200', '201', '202', '967', '968', '070', '071', '072', '073', '074', '075', '076', '077', '078', '079', '060', '061', '062', '063', '064', '065', '066', '067', '068', '069'],
  low: ['386', '387', '388', '389', '390', '391', '392', '393', '394', '395', '396', '397', '350', '351', '352', '353', '354', '355', '356', '357', '358', '359', '360', '361', '362', '363', '364', '365', '366', '367', '368', '369', '716', '717', '718', '719', '720', '721', '722', '723', '724', '725', '726', '727', '728', '729', '247', '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258', '259', '260', '261', '262', '263', '264', '265', '266', '267', '268', '400', '401', '402', '403', '404', '405', '406', '407', '408', '409', '410', '411', '412', '413', '414', '415', '416', '417', '418', '419', '420', '421', '422', '423', '424', '425', '426', '427', '570', '571', '572', '573', '574', '575', '576', '577', '580', '581', '582', '583', '584', '585', '586', '587', '588', '590', '591', '592', '593', '594', '595', '596', '597', '598', '599', '820', '821', '822', '823', '824', '825', '826', '827', '828', '829', '830', '831', '730', '731', '732', '733', '734', '735', '736', '737', '738', '739', '740', '741', '742', '743', '744', '745', '746', '747', '748', '749', '870', '871', '872', '873', '874', '875', '876', '877', '878', '879', '880', '881', '882', '883', '884']
};

export default function PricingCalculator() {
  const [formData, setFormData] = useState({
    service: '',
    duration: '30',
    numDogs: '1',
    petType: 'dog',
    boardingType: 'kennel',
    dogSize: 'medium',
    groomingType: 'full',
    trainingFormat: 'private',
    daycareType: 'full',
    zipCode: '',
    experience: 'Experienced 1-3 years',
    insured: 'Yes',
    currentRate: ''
  });

  const [incomeGoal, setIncomeGoal] = useState('');
  const [showResults, setShowResults] = useState(false);

  // --- LOGIC HELPERS ---

  const zipInfo = useMemo(() => {
    if (formData.zipCode.length < 3) return { tier: 'mid', text: "We'll use this to estimate your local market", emoji: '', color: 'text-brand-mediumPurple/60' };
    const prefix = formData.zipCode.substring(0, 3);
    if (ZIP_RANGES.high.includes(prefix)) return { tier: 'high', text: 'High cost-of-living area (local rates tend to run higher)', emoji: '📍', color: 'text-brand-mediumPurple' };
    if (ZIP_RANGES.low.includes(prefix)) return { tier: 'low', text: 'Lower cost-of-living area (local rates tend to run a bit lower)', emoji: '📍', color: 'text-brand-mediumPurple' };
    return { tier: 'mid', text: 'Mid cost-of-living area', emoji: '📍', color: 'text-brand-mediumPurple' };
  }, [formData.zipCode]);

  const results = useMemo(() => {
    if (!showResults || !formData.service) return null;

    const serviceData = PRICING_DATA[formData.service];
    const tier = formData.zipCode.length === 5 ? zipInfo.tier : 'national';
    
    let baseRate = 0;
    let optionLabel = '';

    // Determine Base Rate based on conditional fields
    if (formData.service === 'dog-walking' || formData.service === 'pet-sitting') {
      baseRate = serviceData[tier === 'national' ? 'national' : tier][formData.duration];
      optionLabel = `${formData.duration}-min ${formData.service === 'dog-walking' ? 'walk' : 'visit'}`;
      if (formData.service === 'dog-walking' && formData.numDogs !== '1') {
        baseRate += serviceData.addons[formData.numDogs === '2' ? '2-dogs' : '3-plus-dogs'];
      }
      if (formData.service === 'pet-sitting' && formData.petType === 'cat') {
        baseRate *= serviceData.modifiers.cat;
      }
    } else if (formData.service === 'boarding') {
      baseRate = serviceData[tier === 'national' ? 'national' : tier][formData.boardingType];
      optionLabel = formData.boardingType.replace('-', ' ');
    } else if (formData.service === 'grooming') {
      baseRate = serviceData[tier === 'national' ? 'national' : tier][formData.dogSize];
      optionLabel = `${formData.dogSize} dog · ${formData.groomingType}`;
      if (formData.groomingType === 'bath-only') baseRate += serviceData.modifiers['bath-only'];
      if (formData.groomingType === 'mobile') baseRate += serviceData.addons.mobile;
    } else if (formData.service === 'training') {
      baseRate = serviceData[tier === 'national' ? 'national' : tier][formData.trainingFormat];
      optionLabel = formData.trainingFormat;
    } else if (formData.service === 'daycare') {
      baseRate = serviceData[tier === 'national' ? 'national' : tier][formData.daycareType];
      optionLabel = `${formData.daycareType} day`;
    }

    // Apply Multipliers
    const expMulti = formData.experience.includes('New') ? 0.88 : formData.experience.includes('Seasoned') ? 1.12 : 1.0;
    let finalRate = baseRate * expMulti;
    if (formData.insured === 'Yes') finalRate += 3;

    // Round to nearest 0.50
    finalRate = Math.round(finalRate * 2) / 2;

    const goalValue = INCOME_GOALS.find(g => g.id === incomeGoal)?.value || 0;
    const sessionsNeeded = Math.ceil(goalValue / finalRate);
    const clientsNeeded = Math.ceil(sessionsNeeded / serviceData.sessionsPerClient);
    
    // Suggested Minimum
    const nationalBase = serviceData.national[Object.keys(serviceData.national)[0]];
    const suggestedMin = Math.max(Math.ceil(800 / serviceData.sessionsPerClient / 4 / 0.5) * 0.5, nationalBase * 0.75);

    return {
      rate: finalRate,
      unit: serviceData.unit,
      optionLabel,
      nationalAvg: serviceData.national[formData.duration] || serviceData.national[formData.boardingType] || serviceData.national[formData.dogSize] || serviceData.national[formData.trainingFormat] || serviceData.national[formData.daycareType],
      tier: zipInfo.tier,
      goalValue,
      sessionsNeeded,
      clientsNeeded,
      suggestedMin
    };
  }, [formData, incomeGoal, showResults, zipInfo]);

  // --- HANDLERS ---

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'zipCode') {
      const val = value.replace(/\D/g, '').substring(0, 5);
      setFormData(prev => ({ ...prev, [name]: val }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const validateAndShow = () => {
    if (!formData.service) {
      alert("Please select a service type first.");
      return;
    }
    if (!incomeGoal) {
      alert("Please pick a monthly income goal first.");
      return;
    }
    setShowResults(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getVerdict = () => {
    if (!formData.currentRate) return null;
    const current = parseFloat(formData.currentRate);
    const national = results.nationalAvg;
    if (current < national * 0.85) return {
      color: 'text-red-500',
      bg: 'bg-red-50',
      title: "You're likely undercharging",
      text: `You are $${(national - current).toFixed(2)} below the national average for this service.`
    };
    if (current > national * 1.15) return {
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      title: "You're priced above market",
      text: "This is fine if your positioning, experience, and certifications support a premium price point."
    };
    return {
      color: 'text-green-600',
      bg: 'bg-green-50',
      title: "You're in a solid range",
      text: `Your pricing is competitive. Focus on acquiring ${results.clientsNeeded} consistent clients to hit your goal.`
    };
  };

  return (
    <>
      <SEO 
        title="Pet Care Pricing Calculator | Otso Co" 
        description="Find your ideal pricing for dog walking, grooming, sitting, and more. Compare local rates and hit your income goals." 
        path="/pricing-calculator" 
      />
      
      <div className="min-h-screen bg-brand-cream font-sans text-brand-darkText">
        {/* Hero Section */}
        <section className="bg-brand-primaryDark text-white py-20 px-4 text-center">
          <div className="max-w-4xl mx-auto">
            <span className="inline-block bg-brand-mediumPurple/20 text-brand-blush px-4 py-1.5 rounded-full text-xs font-bold mb-6">
              Free Tool for Pet Care Businesses
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold mb-4">What Should I Charge?</h1>
            <p className="text-2xl md:text-3xl text-brand-blush italic font-serif mb-8">Find your pricing sweet spot.</p>
            <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              Stop guessing. See how your rates compare to real national data — then find the number that fills your calendar without leaving money on the table.
            </p>
          </div>
        </section>

        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto space-y-8">
            <AnimatePresence mode="wait">
              {!showResults ? (
                <motion.div 
                  key="form"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-8"
                >
                  {/* STEP 1: Service */}
                  <div className="bg-white p-8 rounded-[24px] border-2 border-gray-100 shadow-sm">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                      <span className="w-8 h-8 bg-brand-blush/30 rounded-full flex items-center justify-center text-sm">1</span>
                      What service do you offer?
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {SERVICES.map(s => (
                        <button
                          key={s.id}
                          onClick={() => setFormData(prev => ({ ...prev, service: s.id }))}
                          className={`p-4 rounded-xl border-2 transition-all text-center flex flex-col items-center gap-2 ${
                            formData.service === s.id 
                            ? 'border-brand-purple bg-brand-purple/5 shadow-inner' 
                            : 'border-gray-100 hover:border-brand-purple/30 bg-white'
                          }`}
                        >
                          <span className="text-3xl">{s.icon}</span>
                          <span className="text-sm font-bold">{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* STEP 2: Business Details */}
                  <div className="bg-white p-8 rounded-[24px] border-2 border-gray-100 shadow-sm">
                    <h2 className="text-xl font-bold mb-8 flex items-center gap-2">
                      <span className="w-8 h-8 bg-brand-blush/30 rounded-full flex items-center justify-center text-sm">2</span>
                      Tell me about your business
                    </h2>
                    
                    <div className="space-y-8">
                      {/* Conditional Fields */}
                      {formData.service === 'dog-walking' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-brand-cream/30 rounded-2xl border border-gray-100">
                          <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-widest text-gray-500">Walk Duration</label>
                            <select name="duration" value={formData.duration} onChange={handleInputChange} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all">
                              <option value="15">15 min</option>
                              <option value="30">30 min</option>
                              <option value="60">60 min</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-widest text-gray-500">Number of Dogs</label>
                            <select name="numDogs" value={formData.numDogs} onChange={handleInputChange} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all">
                              <option value="1">1 Dog</option>
                              <option value="2">2 Dogs</option>
                              <option value="3-plus-dogs">3+ Dogs</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {formData.service === 'pet-sitting' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-brand-cream/30 rounded-2xl border border-gray-100">
                          <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-widest text-gray-500">Visit Length</label>
                            <select name="duration" value={formData.duration} onChange={handleInputChange} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all">
                              <option value="15">15 min</option>
                              <option value="30">30 min</option>
                              <option value="60">60 min</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-widest text-gray-500">Pet Type</label>
                            <select name="petType" value={formData.petType} onChange={handleInputChange} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all">
                              <option value="dog">Dog</option>
                              <option value="cat">Cat</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {formData.service === 'boarding' && (
                        <div className="space-y-2 p-6 bg-brand-cream/30 rounded-2xl border border-gray-100">
                          <label className="text-xs font-black uppercase tracking-widest text-gray-500">Boarding Type</label>
                          <select name="boardingType" value={formData.boardingType} onChange={handleInputChange} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all">
                            <option value="kennel">Kennel / Facility</option>
                            <option value="sitter">Sitter's Home</option>
                            <option value="in-home">In Client's Home</option>
                            <option value="overnight">Overnight Daycare</option>
                          </select>
                        </div>
                      )}

                      {formData.service === 'grooming' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-brand-cream/30 rounded-2xl border border-gray-100">
                          <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-widest text-gray-500">Dog Size</label>
                            <select name="dogSize" value={formData.dogSize} onChange={handleInputChange} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all">
                              <option value="small">Small (under 20 lbs)</option>
                              <option value="medium">Medium (20-50 lbs)</option>
                              <option value="large">Large (50-80 lbs)</option>
                              <option value="xlarge">Extra Large (80+ lbs)</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-black uppercase tracking-widest text-gray-500">Service Type</label>
                            <select name="groomingType" value={formData.groomingType} onChange={handleInputChange} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all">
                              <option value="full">Full Groom</option>
                              <option value="bath-only">Bath & Brush Only</option>
                              <option value="mobile">Mobile Grooming</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Always Visible */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <label className="text-xs font-black uppercase tracking-widest text-gray-500">Your ZIP Code</label>
                          <input 
                            type="text" 
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={handleInputChange}
                            placeholder="e.g. 20707"
                            className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all"
                          />
                          <p className={`text-[11px] font-bold ${zipInfo.color} transition-colors`}>
                            {zipInfo.emoji} {zipInfo.text}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-black uppercase tracking-widest text-gray-500">Experience Level</label>
                          <select name="experience" value={formData.experience} onChange={handleInputChange} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all">
                            <option>New under 1 year</option>
                            <option>Experienced 1-3 years</option>
                            <option>Seasoned pro 3+ years</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-black uppercase tracking-widest text-gray-500">Insured / Bonded</label>
                          <select name="insured" value={formData.insured} onChange={handleInputChange} className="w-full p-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all">
                            <option>Yes</option>
                            <option>No</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-black uppercase tracking-widest text-gray-500">Current Rate (Optional)</label>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                            <input 
                              type="number" 
                              name="currentRate"
                              value={formData.currentRate}
                              onChange={handleInputChange}
                              placeholder="e.g. 25"
                              className="w-full pl-8 pr-4 py-4 rounded-xl border-2 border-gray-100 focus:border-brand-purple bg-brand-cream outline-none transition-all"
                            />
                          </div>
                          <p className="text-[10px] text-gray-400 font-medium italic">Leave blank to skip the comparison</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* STEP 3: Income Goal */}
                  <div className="bg-white p-8 rounded-[24px] border-2 border-gray-100 shadow-sm">
                    <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                       <span className="w-8 h-8 bg-brand-blush/30 rounded-full flex items-center justify-center text-sm">3</span>
                       What's your monthly income goal?
                    </h2>
                    <div className="grid grid-cols-2 gap-4">
                      {INCOME_GOALS.map(goal => (
                        <button
                          key={goal.id}
                          onClick={() => setIncomeGoal(goal.id)}
                          className={`p-4 rounded-xl border-2 transition-all text-left ${
                            incomeGoal === goal.id 
                            ? 'border-brand-purple bg-brand-purple/5 shadow-inner' 
                            : 'border-gray-100 hover:border-brand-purple/30 bg-white'
                          }`}
                        >
                          <span className="block text-lg font-bold">{goal.label}</span>
                          <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">{goal.sub}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button 
                    onClick={validateAndShow}
                    className="w-full bg-brand-purple hover:bg-brand-purple/90 text-white py-5 rounded-xl font-black text-xl transition-all shadow-xl flex items-center justify-center gap-3"
                  >
                    Show My Pricing <SafeIcon icon={FiArrowRight} />
                  </button>
                </motion.div>
              ) : (
                <motion.div 
                  key="results"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-8"
                >
                  {/* Hero Result */}
                  <div className="bg-brand-primaryDark text-white p-10 rounded-[32px] text-center shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-10 opacity-5">
                      <SafeIcon icon={FiTarget} size={150} />
                    </div>
                    <p className="text-brand-blush font-black uppercase tracking-widest text-xs mb-2">Your Recommended Rate</p>
                    <h3 className="text-6xl md:text-7xl font-serif font-bold mb-4">${results.rate.toFixed(2)}</h3>
                    <p className="text-white/70 font-bold uppercase tracking-widest text-sm">
                      per {results.unit} · {results.optionLabel}
                    </p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 md:gap-6">
                    <div className="bg-white p-6 rounded-[24px] border-2 border-gray-100 shadow-sm">
                      <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1">National Average</p>
                      <p className="text-2xl font-bold text-brand-primaryDark">${results.nationalAvg.toFixed(2)}</p>
                      <p className="text-[10px] text-gray-500 font-medium">National market average</p>
                    </div>
                    <div className="bg-white p-6 rounded-[24px] border-2 border-gray-100 shadow-sm">
                      <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1">Your Region</p>
                      <p className="text-2xl font-bold text-brand-primaryDark capitalize">{results.tier}</p>
                      <p className="text-[10px] text-gray-500 font-medium">
                        {formData.zipCode ? `Based on ZIP ${formData.zipCode} area` : 'National fallback data'}
                      </p>
                    </div>
                    <div className="bg-white p-6 rounded-[24px] border-2 border-gray-100 shadow-sm">
                      <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1">Clients Needed / Mo</p>
                      <p className="text-2xl font-bold text-brand-primaryDark">{results.clientsNeeded}</p>
                      <p className="text-[10px] text-gray-500 font-medium">To hit ${results.goalValue.toLocaleString()}/mo</p>
                    </div>
                    <div className="bg-white p-6 rounded-[24px] border-2 border-gray-100 shadow-sm">
                      <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1">Suggested Minimum</p>
                      <p className="text-2xl font-bold text-brand-primaryDark">${results.suggestedMin.toFixed(2)}</p>
                      <p className="text-[10px] text-gray-500 font-medium">The floor, not the goal</p>
                    </div>
                  </div>

                  {/* Comparison Card */}
                  {formData.currentRate && (
                    <div className="bg-white p-8 rounded-[24px] border-2 border-gray-100 shadow-sm">
                      <h4 className="font-bold text-brand-primaryDark mb-6 flex items-center gap-2">
                        <SafeIcon icon={FiTrendingUp} className="text-brand-purple" />
                        Market Comparison
                      </h4>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center py-3 border-b border-gray-50">
                          <span className="text-sm text-gray-500 font-medium">Your Current Rate</span>
                          <span className="font-bold font-mono">${parseFloat(formData.currentRate).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-50">
                          <span className="text-sm text-gray-500 font-medium">National Average</span>
                          <span className="font-bold font-mono">${results.nationalAvg.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-3 border-b border-gray-50">
                          <span className="text-sm text-gray-500 font-medium">Recommended Rate</span>
                          <span className="font-bold font-mono text-brand-purple">${results.rate.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-3">
                          <span className="text-sm text-gray-500 font-medium">Where You Stand</span>
                          {(() => {
                            const diff = parseFloat(formData.currentRate) - results.nationalAvg;
                            if (diff < -3) return <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">${Math.abs(diff).toFixed(2)} below market</span>;
                            if (diff > 8) return <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold">${diff.toFixed(2)} above market</span>;
                            return <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold">Near market rate</span>;
                          })()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Verdict Box */}
                  {getVerdict() && (
                    <div className={`${getVerdict().bg} p-6 rounded-2xl border border-gray-100 flex gap-4`}>
                      <SafeIcon icon={FiAlertCircle} className={`${getVerdict().color} shrink-0`} size={24} />
                      <div>
                        <h5 className={`font-black uppercase tracking-wider text-xs ${getVerdict().color} mb-1`}>
                          Verdict: {getVerdict().title}
                        </h5>
                        <p className="text-gray-700 text-sm leading-relaxed">{getVerdict().text}</p>
                      </div>
                    </div>
                  )}

                  {/* What to do next */}
                  <div className="bg-white p-8 rounded-[24px] border-2 border-gray-100 shadow-sm">
                    <h4 className="text-lg font-bold text-brand-primaryDark mb-8">What To Do Next</h4>
                    <div className="space-y-6">
                      {[
                        { icon: '📍', text: "Research 3 local competitors in your exact zip code. Online averages are a starting point, but your neighborhood might run $5-$10 higher or lower." },
                        { icon: '💬', text: "Don't lead with your price. Lead with your value — certifications, insurance, GPS updates, photos, experience. Then price accordingly." },
                        { icon: '📅', text: "If you're raising rates, give existing clients 30 days notice. Most will stay. The ones who don't weren't your ideal clients anyway." },
                        { icon: '📈', text: `At $${results.rate.toFixed(2)} per ${results.unit}, you need ${results.sessionsNeeded} sessions per month to hit your goal. That's ${Math.ceil(results.sessionsNeeded / 4)} sessions per week — totally doable with the right social strategy.` }
                      ].map((item, i) => (
                        <div key={i} className="flex gap-4">
                          <div className="w-10 h-10 bg-brand-softPurple/10 rounded-full flex items-center justify-center shrink-0 text-lg">
                            {item.icon}
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed font-medium pt-1">{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="bg-brand-lavender p-10 rounded-[32px] text-white text-center shadow-xl">
                    <h3 className="text-2xl font-bold mb-4">Want a custom strategy built around these numbers?</h3>
                    <p className="text-white/80 mb-10 leading-relaxed">
                      Otso Co works exclusively with pet care businesses — dog walkers, groomers, trainers, boarders, sitters, daycares, and pet stores. We help you fill your calendar without ads.
                    </p>
                    <a 
                      href="https://otsoco.com" 
                      className="bg-white text-brand-primaryDark px-10 py-5 rounded-xl font-bold text-lg hover:bg-brand-blush transition-all inline-flex items-center gap-2"
                    >
                      Work With Otso Co <SafeIcon icon={FiArrowRight} />
                    </a>
                  </div>

                  <p className="text-[11px] text-center text-gray-400 font-medium leading-relaxed px-10">
                    Rate data sourced from Time To Pet's rate calculator (2024-2025), Rover (March 2026), Thumbtack, and Airtasker. Rates vary by location, experience, and services offered. Always research your local market.
                  </p>

                  <button 
                    onClick={() => {
                      setShowResults(false);
                      setIncomeGoal('');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="w-full text-gray-400 font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:text-brand-primaryDark transition-colors py-4"
                  >
                    <SafeIcon icon={FiRefreshCw} /> Start Over
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>
      </div>
    </>
  );
}